"use client";

import { useMemo, useState } from "react";
import { usePublisherFollowersHistory } from "../hooks/usePublisherFollowersHistory";
import {
  FOLLOWERS_RANGE_OPTIONS,
  CHART_HEIGHT_DESKTOP,
  CHART_HEIGHT_MOBILE,
  RangeSelector,
  ChartContainer,
  useIsMobile,
  useChartOptions,
} from "@/components/shared/rank-history";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";

export function PublisherFollowersHistory({ clanid }: { clanid: number }) {
  const { elementRef, isVisible } = useIntersectionObserver({
    rootMargin: "200px",
  });

  const defaultRangeKey = "7d";
  const [selectedRangeKey, setSelectedRangeKey] =
    useState<string>(defaultRangeKey);
  const [bust, setBust] = useState<number | undefined>(undefined);
  const primaryColor = "#66c0f4";

  const resolution =
    selectedRangeKey === "7d" ||
    selectedRangeKey === "30d" ||
    selectedRangeKey === "3m"
      ? "daily"
      : selectedRangeKey === "1y" ||
        selectedRangeKey === "3y" ||
        selectedRangeKey === "5y" ||
        selectedRangeKey === "all"
      ? "weekly"
      : "monthly";

  const { history, isLoading, error } = usePublisherFollowersHistory({
    clanid,
    range: selectedRangeKey,
    resolution,
    bust,
    enabled: Boolean(clanid) && isVisible,
  });

  const chartData = useMemo(
    () =>
      history
        .filter((p) => p.followers !== null)
        .map((p) => [p.ts * 1000, p.followers] as [number, number]),
    [history]
  );

  const isMobile = useIsMobile();
  const chartOptions = useChartOptions(chartData, isMobile, primaryColor, true);
  const chartHeight = isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT_DESKTOP;

  const handleRangeChange = (key: string) => {
    setSelectedRangeKey(key);
  };

  const handleRefresh = () => {
    setBust(Date.now());
  };

  return (
    <div
      ref={elementRef}
      className="bg-[#16202d] rounded-lg border border-[#2a475e] p-3 md:p-6 space-y-3 md:space-y-4"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-white">
            Followers History
          </h3>
          <p className="text-[#8f98a0] text-xs md:text-sm">
            Publisher followers count across the selected period.
          </p>
        </div>

        <RangeSelector
            chart="publisher_followers"
          selectedRangeKey={selectedRangeKey}
          onRangeChange={handleRangeChange}
          onRefresh={handleRefresh}
          isLoading={isLoading}
          isMobile={isMobile}
          primaryColor={primaryColor}
          rangeOptions={FOLLOWERS_RANGE_OPTIONS}
        />
      </div>

      <ChartContainer
            chart="publisher_followers"
        chartOptions={chartOptions}
        isLoading={isLoading}
        error={error}
        hasData={history.length > 0}
        height={chartHeight}
        primaryColor={primaryColor}
      />
    </div>
  );
}

